/**
 * Gespräch am Tischtelefon — die Regeln ohne React.
 *
 * Auf derselben Nebenstelle hängen Browser-Softphone (Linkus-Web-SDK),
 * Linkus-App und Tischtelefon. Nimmt jemand am Tischtelefon ab, entzieht die
 * Anlage dem Browser den Anruf: das SDK beendet seine Session, und KMVS wüsste
 * vom Gespräch nichts mehr. Die Anlage meldet die Annahme aber per Webhook
 * (30011 ANSWERED → SSE `incoming`, egal an welchem Gerät). Kommt dieses
 * Signal und das Softphone führt KEIN Gespräch, läuft es an einem anderen
 * Gerät — dann zeigt das Fenster den Anruf weiter, nur ohne Steuerung
 * (ADR 0001, Nachtrag).
 *
 * Achtung: Die callId der SSE-Ereignisse ist die der Anlage
 * („1791370702.10401"), NICHT die UUID des SDK. Unter ihr legt der CDR die
 * `CallLog`-Zeile an — und dort gehört die Notiz hin.
 */

import type { TelephonyEvent } from "../../services/telephonyService";

/**
 * Läuft im Browser gerade ein „Annehmen", wartet die Entscheidung in diesem
 * Takt auf das SDK-`accepted` …
 */
export const TISCHTELEFON_ENTSCHEIDUNG_MS = 300;
/** … höchstens bis hierhin; dann gilt das andere Gerät. */
export const TISCHTELEFON_ENTSCHEIDUNG_MAX_MS = 6_000;
/** Hat das Softphone so kurz vor/nach dem Signal angenommen, war es der Browser. */
export const SDK_ANNAHME_FENSTER_MS = 5_000;
/** Nach `ended` so lange auf den CDR (und damit die `CallLog`-ID) warten. */
export const TISCHTELEFON_CDR_WARTEN_MS = 15_000;
/** Sicherheitsnetz, falls `ended` UND `cdr` verloren gehen: länger steht das Fenster nie. */
export const TISCHTELEFON_HOECHSTDAUER_MS = 4 * 60 * 60 * 1000;
/**
 * Eine beim Klingeln im Softphone getippte Notiz zieht mit ins
 * Tischtelefon-Gespräch um, wenn dieses binnen dieser Frist beginnt.
 */
export const KLINGEL_NOTIZ_FRIST_MS = 10_000;

export interface TischtelefonAnruf {
    /** callId der Anlage — Schlüssel der CDR-Zeile. */
    pbxCallId: string;
    direction: "inbound" | "outbound";
    /** Die Gegenseite (eingehend: Anrufer, ausgehend: Ziel). */
    number: string | null;
    /** KMVS-Kontaktname, den das Backend zur Nummer gefunden hat. */
    name: string | null;
    contactId: number | null;
    /** ms-Timestamp der Annahme (Eingang des Signals im Browser). */
    answeredAt: number;
}

const ARTEN = new Set(["incoming", "status", "ended", "cdr", "uebergabe"]);

/**
 * Einen SSE-Frame lesen. Heartbeats (`ping`, leere callId), Kaputtes und
 * Unbekanntes ergeben `null`.
 */
export function ereignisLesen(data: unknown): TelephonyEvent | null {
    if (typeof data !== "string" || !data) return null;
    let evt: TelephonyEvent;
    try {
        evt = JSON.parse(data) as TelephonyEvent;
    } catch {
        return null;
    }
    if (!evt || typeof evt !== "object") return null;
    if (!ARTEN.has(evt.kind) || typeof evt.callId !== "string" || !evt.callId) return null;
    return evt;
}

/**
 * Aus einem `incoming`-Ereignis den Anruf fürs Fenster machen. Interne
 * Gespräche (Durchwahl ↔ Durchwahl) tragen keine Gegennummer und bleiben
 * außen vor — wie bei der Nach-Anruf-Karte.
 */
export function tischtelefonAnrufAus(evt: TelephonyEvent, jetzt: number): TischtelefonAnruf | null {
    if (evt.kind !== "incoming") return null;
    const p = evt.payload ?? {};
    if (p.direction === "internal") return null;
    // Ohne Richtung (älteres Backend) war es bislang immer ein eingehender Anruf.
    const direction = p.direction === "outbound" ? "outbound" : "inbound";
    const number = ((direction === "outbound" ? p.toNumber : p.fromNumber) ?? "").trim() || null;
    if (!number) return null;
    return {
        pbxCallId: evt.callId,
        direction,
        number,
        name: p.contactDisplayName?.trim() || null,
        contactId: p.contactId ?? null,
        answeredAt: jetzt,
    };
}

/** Was das Softphone gerade weiß — Auszug aus `softphone.getState()`. */
export interface SdkLage {
    anruf: { direction: "inbound" | "outbound"; state: string; answeredAt: number | null } | null;
    /** Zeitpunkt der letzten Annahme im Browser (auch eines schon beendeten Gesprächs). */
    letzteAnnahme: number | null;
    /** Ein Klick auf „Annehmen" liegt gerade beim SDK (`softphone.annehmenLaeuft()`). */
    annehmenLaeuft?: boolean;
}

export type GeraetEntscheidung = "browser" | "tischtelefon" | "warten";

/**
 * Wo läuft das gerade angenommene Gespräch?
 *  - Führt das Softphone ein angenommenes Gespräch (oder hat es eben eins
 *    angenommen) → im Browser, das Fenster gibt es schon.
 *  - Wählt das Softphone gerade selbst → auch der Browser (die Anlage meldet
 *    ANSWERED mitunter, bevor die Gegenseite abhebt).
 *  - Liegt ein Klick auf „Annehmen" gerade beim SDK, auf dessen `accepted`
 *    warten (höchstens `TISCHTELEFON_ENTSCHEIDUNG_MAX_MS`).
 *  - Sonst SOFORT das andere Gerät — auch wenn das Softphone noch klingelt:
 *    Die Anlage meldet „angenommen", und im Browser hat niemand angenommen;
 *    sein Klingeln bricht die Anlage gleich ab (07.10.2026: „wenn ich den
 *    Hörer abnehme, dann das Popup als aktives Telefonat, mit Minuten,
 *    Sekunden" — vorher stand bis zu 6 s gar kein Fenster da).
 *
 * Warum das genügt: Das SDK meldet `accepted`, sobald es die Annahme
 * abschickt — also bevor die Anlage sie per Webhook weitergeben kann.
 */
export function entscheideGeraet(lage: SdkLage, ereignisZeit: number, jetzt: number): GeraetEntscheidung {
    const a = lage.anruf;
    if (a && (a.answeredAt != null || a.state === "talking")) return "browser";
    if (lage.letzteAnnahme != null && Math.abs(ereignisZeit - lage.letzteAnnahme) <= SDK_ANNAHME_FENSTER_MS) {
        return "browser";
    }
    if (a && a.direction === "outbound") return "browser";
    if (lage.annehmenLaeuft && jetzt - ereignisZeit < TISCHTELEFON_ENTSCHEIDUNG_MAX_MS) return "warten";
    return "tischtelefon";
}

/**
 * Ist das Ereignis das Ende DIESES Gesprächs? `ended` kommt beim Auflegen,
 * `cdr` kurz danach — der CDR zählt zusätzlich, falls `ended` in einer
 * Verbindungslücke verloren ging (Lehre aus #1226).
 */
export function endeSignal(evt: TelephonyEvent, pbxCallId: string): "ended" | "cdr" | null {
    if (evt.callId !== pbxCallId) return null;
    if (evt.kind === "ended") return "ended";
    if (evt.kind === "cdr") return "cdr";
    return null;
}

/**
 * Aus der Liste `GET /telephony/aktive-anrufe` das Gespräch fürs Fenster nach
 * einem Neuladen: das jüngste darstellbare. Die Dauer zählt ab der Annahme laut
 * Anlage (`startedAt`), nicht ab dem Neuladen.
 */
export function wiederherzustellen(liste: readonly TelephonyEvent[], jetzt: number): TischtelefonAnruf | null {
    for (let i = liste.length - 1; i >= 0; i--) {
        const evt = liste[i];
        const seit = Date.parse(evt.payload?.startedAt ?? "");
        const anruf = tischtelefonAnrufAus(evt, Number.isFinite(seit) ? Math.min(seit, jetzt) : jetzt);
        if (anruf) return anruf;
    }
    return null;
}

/** Die `CallLog`-ID aus einem `cdr`-Ereignis. */
export function callLogIdAus(evt: TelephonyEvent): number | null {
    const id = evt.kind === "cdr" ? evt.payload?.callLogId : null;
    return typeof id === "number" && Number.isFinite(id) ? id : null;
}

/** Wartezeit vor dem n-ten Neuaufbau des Ereignisstroms (2 s, 4 s, 8 s … höchstens 30 s). */
export function wiederverbindenNach(versuch: number): number {
    const n = Math.max(0, Math.floor(versuch));
    return Math.min(30_000, 2_000 * 2 ** Math.min(n, 10));
}

/**
 * Dieselbe Rufnummer? Formate unterscheiden sich zwischen SDK und Anlage
 * („+49 711 …", „0049711…", „0711…") — verglichen werden die letzten neun
 * Ziffern, bei kurzen Nummern die ganze Ziffernfolge.
 */
export function gleicheRufnummer(a: string | null | undefined, b: string | null | undefined): boolean {
    const x = (a ?? "").replace(/\D/g, "");
    const y = (b ?? "").replace(/\D/g, "");
    if (!x || !y) return false;
    if (x.length < 9 || y.length < 9) return x === y;
    return x.slice(-9) === y.slice(-9);
}

export interface KlingelNotizKandidat {
    /** SDK-callId des im Browser nicht angenommenen Anrufs. */
    callId: string;
    number: string | null;
    endedAt: number;
}

/**
 * Welcher eben im Softphone verklungene Anruf ist das Gespräch, das jetzt am
 * Tischtelefon läuft? Der jüngste mit derselben Nummer innerhalb der Frist.
 */
export function passendeKlingelNotiz(
    kandidaten: readonly KlingelNotizKandidat[],
    anruf: Pick<TischtelefonAnruf, "number" | "direction">,
    jetzt: number,
): string | null {
    if (anruf.direction !== "inbound") return null;
    let treffer: KlingelNotizKandidat | null = null;
    for (const k of kandidaten) {
        if (jetzt - k.endedAt > KLINGEL_NOTIZ_FRIST_MS || k.endedAt - jetzt > KLINGEL_NOTIZ_FRIST_MS) continue;
        if (!gleicheRufnummer(k.number, anruf.number)) continue;
        if (!treffer || k.endedAt > treffer.endedAt) treffer = k;
    }
    return treffer?.callId ?? null;
}

/** Zwei Notiz-Texte zusammenführen, ohne einen zu verlieren. */
export function notizenZusammen(bisher: string, dazu: string): string {
    const a = bisher.trim();
    const b = dazu.trim();
    if (!a) return dazu;
    if (!b) return bisher;
    return `${dazu.trimEnd()}\n${bisher}`;
}
